import axios from 'axios';
import { API_URL, CONTACTS_API_URL } from '../config/api';
import SessionService from './SessionService';

// Interface para a resposta da API
interface ApiResponse<T> {
  message: string;
  code: string;
  data: T[];
}

// Interface para o contato
export interface Contact {
  isViewed: boolean;
  is_viewed: any;
  id: number;
  name: string;
  tagId: number | null;
  number: string;
  avatarUrl: string | null;
  email: string;
  notes: string | null;
  sectorId: number;
  isActive: boolean;
  priority: string;
  contactStatus: string;
  aiActive: boolean;
  assignedTo: number | null;
  responsibleName?: string | null;
  createdAt: string;
  updatedAt: string;
  isOfficial: boolean;
  order?: number;
  archived?: boolean;
}

export interface CreateContactRequestDTO {
  name: string;
  phoneNumber: string;
  email: string;
  sectorId: number;
  status?: string;
  notes?: string;
}

export interface UpdateContactRequestDTO {
  name: string;
  tagId: number | null;
  phoneWhatsapp: string;
  avatarUrl: string | null;
  email: string;
  notes: string | null;
  sectorId: number;
  isActive: boolean;
  priority: string;
  aiActive: boolean;
  contactStatus?: string;
  assignedTo: number | null;
}

export interface UpdateResponsibleRequestDTO {
  responsibleId: number;
}

export interface ArchiveContactRequestDTO {
  contactId: number;
  archived: boolean;
}

export interface ListContactsRequestDTO {
  sectorId: number;
  includeArchived: boolean;
  onlyArchived: boolean;
}

export const getContacts = async (sectorId: number): Promise<ApiResponse<Contact>> => {
  try {
    const token = SessionService.getToken();

    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const url = `${CONTACTS_API_URL}/Contact/sector/${sectorId}`;
    const response = await axios.get<ApiResponse<Contact>>(
      url,
      {
        headers: {
          'Accept': '*/*',
          'Authorization': `Bearer ${token}`
        },
      }
    );


    return response.data;
  } catch (error) {
    console.error('Erro detalhado em getContacts:', {
      error,
      message: error instanceof Error ? error.message : 'Erro desconhecido',
    });
    throw new Error('Falha ao obter contatos: ' + error);
  }
};

export const getContactById = async (id: number): Promise<Contact | null> => {
  try {
    const token = SessionService.getToken();

    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const response = await axios.get(`${CONTACTS_API_URL}/contact/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    return null;
  }
};

export const createContact = async (contact: CreateContactRequestDTO): Promise<Contact | null> => {
  try {
    const token = SessionService.getToken();

    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const response = await axios.post(`${CONTACTS_API_URL}/contact`, contact, {
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });


    // Extrair dados da resposta baseado na estrutura do backend
    let contactData = null;
    if (response.data && response.data.data) {
      contactData = response.data.data;
    } else if (response.data) {
      contactData = response.data;
    }

    if (contactData) {
      // Normalizar dados para garantir compatibilidade com a interface
      const normalizedContact: Contact = {
        id: contactData.id,
        name: contactData.name || '',
        number: contactData.number || '',
        avatarUrl: contactData.avatarUrl || null,
        email: contactData.email || '',
        notes: contactData.notes || null,
        sectorId: contactData.sectorId,
        tagId: contactData.tagId || null,
        isActive: contactData.isActive !== undefined ? contactData.isActive : true,
        priority: contactData.priority || 'low',
        contactStatus: contactData.contactStatus || 'Novo',
        aiActive: contactData.aiActive !== undefined ? contactData.aiActive : true,
        assignedTo: contactData.assignedTo || null,
        createdAt: contactData.createdAt || new Date().toISOString(),
        updatedAt: contactData.updatedAt || new Date().toISOString(),
        isOfficial: contactData.isOfficial !== undefined ? contactData.isOfficial : true,
        isViewed: contactData.isViewed !== undefined ? contactData.isViewed : true,
        is_viewed: contactData.is_viewed || contactData.isViewed || true,
        order: contactData.order || 0,
        archived: contactData.archived || false
      };

      return normalizedContact;
    }

    return null;
  } catch (error) {
    throw error;
  }
};

export const updateContact = async (id: number, contact: UpdateContactRequestDTO): Promise<Contact> => {
  try {
    const token = SessionService.getToken();

    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const response = await axios.put<Contact>(
      `${API_URL}/Contact/${id}`,
      contact,
      {
        headers: {
          'Accept': '*/*',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(`Failed to update contact with id ${id}: ${error}`);
  }
};

export const updateResponsible = async (id: number, data: UpdateResponsibleRequestDTO) => {
  try {
    const token = SessionService.getToken();

    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const response = await axios.put(`${CONTACTS_API_URL}/contact/${id}/responsible`, data, {
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(`Failed to update responsible for contact with id ${id}: ${error}`);
  }
};

/** Notifica via WebSocket que um contato foi transferido (para exibir notificação no sino do usuário que recebeu) */
export const notifyContactResponsible = async (contactId: number, sectorId: number, responsibleId: number, responsibleName: string, contactName?: string) => {
  try {
    const token = SessionService.getToken();
    if (!token) return;

    await axios.post(
      `${CONTACTS_API_URL}/notify-contact-responsible`,
      { contactId, sectorId, responsibleId, responsibleName, contactName: contactName || '' },
      {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
  } catch (error) {
    console.warn('[ContactService] Falha ao enviar notificação de transferência:', error);
  }
};

export const getContact = async (id: number): Promise<Contact> => {
  try {
    const token = SessionService.getToken();

    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const response = await axios.get(`${CONTACTS_API_URL}/contact/${id}`, {
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(`Failed to get contact with id ${id}: ${error}`);
  }
};

export const deleteContact = async (id: number) => {
  try {
    const token = SessionService.getToken();

    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const response = await axios.delete(`${CONTACTS_API_URL}/contact/${id}`, {
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(`Failed to delete contact with id ${id}: ${error}`);
  }
};

export const deleteContactFromSector = async (contactId: number, sectorId: number) => {
  try {
    const token = SessionService.getToken();

    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const url = `${CONTACTS_API_URL}/contact/${contactId}/sector/${sectorId}`;
    const response = await axios.delete(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(`Failed to delete contact ${contactId} from sector ${sectorId}: ${error}`);
  }
};

export const markContactAsViewed = async (sectorId: number, contactId: number) => {
  try {
    const token = SessionService.getToken();

    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const url = `${API_URL}/Contact/mark-as-viewed?sectorId=${sectorId}&contactId=${contactId}`;
    await axios.post(url, '', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': '*/*'
      }
    });
    return true;
  } catch (error) {
    return false;
  }
};

export const markContactAsUnread = async (sectorId: number, contactId: number) => {
  try {
    const token = SessionService.getToken();

    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const url = `${API_URL}/Contact/mark-as-unread?sectorId=${sectorId}&contactId=${contactId}`;
    await axios.post(url, '', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': '*/*'
      }
    });
    return true;
  } catch (error) {
    return false;
  }
};


export const archiveContact = async (contactId: number, archived: boolean): Promise<any> => {
  try {
    const token = SessionService.getToken();

    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const requestBody = {
      contactId: contactId,
      archived: archived
    };

    const response = await axios.post(
      `${CONTACTS_API_URL}/contacts/archive`,
      requestBody,
      {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const setContactAIActive = async (contactId: number, aiActive: boolean): Promise<any> => {
  try {
    const token = SessionService.getToken();
    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const response = await axios.patch(
      `${API_URL}/Contact/${contactId}/ai-active`,
      { aiActive },
      {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(`Failed to set AI active for contact ${contactId}: ${error}`);
  }
};

export const getContactsWithFilters = async (sectorId: number, includeArchived: boolean = false, onlyArchived: boolean = false): Promise<ApiResponse<Contact>> => {
  try {
    const token = SessionService.getToken();

    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const requestBody = {
      sectorId: sectorId,
      includeArchived: includeArchived,
      onlyArchived: onlyArchived
    };

    const response = await axios.post<ApiResponse<Contact>>(
      `${CONTACTS_API_URL}/contacts/list`,
      requestBody,
      {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );


    return response.data;
  } catch (error) {
    throw error;
  }
};
